import { ICustomerVehicleInvoicesHook } from '@models';
import { InvoiceService } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface IProps {
  registrationNumber: string | undefined;
  customerId: string | undefined;
}

export const useCustomerVehicleInvoices = ({
  registrationNumber,
  customerId,
}: IProps): ICustomerVehicleInvoicesHook => {
  const { invoiceStore } = useStore();
  const {
    loading,
    invoices,
    totalCount,
    skipCount,
    maxResultCount,
    currentPage,
    setSkipCount,
    setCurrentPage,
  } = invoiceStore;

  const [number, setNumber] = useState<string>();
  const [status, setStatus] = useState<string[]>();
  const [datePreset, setDatePreset] = useState<string | undefined>();
  const [customFrom, setCustomFrom] = useState<string | undefined>();
  const [customTo, setCustomTo] = useState<string | undefined>();
  const [sorting, setSorting] = useState<string | undefined>();

  const statusParam = useMemo(() => (status?.length ? status.join(',') : undefined), [status]);

  const { startAt, endAt } = useMemo(
    () =>
      datePreset === 'custom'
        ? { startAt: customFrom, endAt: customTo }
        : getDateRangeFromPreset(datePreset),
    [datePreset, customFrom, customTo]
  );

  useEffect(() => {
    customerId &&
      invoiceStore.getAll({
        customerId,
        status: statusParam,
        number,
        registrationNumber,
        startAt,
        endAt,
        maxResultCount,
        skipCount,
        sorting,
      });
  }, [
    invoiceStore,
    customerId,
    statusParam,
    number,
    registrationNumber,
    startAt,
    endAt,
    maxResultCount,
    skipCount,
    sorting,
  ]);

  useEffect(() => {
    return () => invoiceStore.resetInvoices();
  }, [invoiceStore]);

  const handlePageChange = (pagination: any) => {
    invoiceStore.setMaxResultCount(pagination.pageSize);
    invoiceStore.setSkipCount((pagination.current - 1) * pagination.pageSize);
    invoiceStore.setCurrentPage(pagination.current);
  };

  const handleSortChange = (value?: string) => {
    setSorting(value);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleNumberSearch = debounce((text: string) => {
    setNumber(text);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleStatusChange = (values?: string[]) => {
    setStatus(values?.length ? values : undefined);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset || undefined);
    if (preset !== 'custom') {
      setCustomFrom(undefined);
      setCustomTo(undefined);
    }
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleCustomDateRange = (from?: string, to?: string) => {
    setCustomFrom(from);
    setCustomTo(to);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await InvoiceService.getAll({
      customerId,
      status: statusParam,
      number,
      registrationNumber,
      startAt,
      endAt,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
      sorting,
    });
    return result.items;
  }, [customerId, statusParam, number, registrationNumber, startAt, endAt, totalCount, sorting]);

  return {
    loading,
    dataSource: Array.from(invoices.values()),
    totalCount,
    maxResultCount,
    handleNumberSearch,
    currentPage,
    handleStatusChange,
    datePreset,
    customFrom,
    customTo,
    handleDatePresetChange,
    handleCustomDateRange,
    handlePageChange,
    handleSortChange,
    sorting,
    fetchAllForExport,
  };
};
